import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Save,
  QrCode,
  Refresh,
  Print,
  CheckCircle,
  Cancel,
  Schedule,
  Person
} from '@mui/icons-material';
import { usePermissions } from '../hooks/usePermissions';

// Normalisation d un libelle pour comparaison : minuscules, sans espaces ni separateurs.
const normText = (str = '') => String(str || '').toLowerCase().replace(/[\s\-_.]/g, '');

// Identifiant d un document, qu il soit un ObjectId, un entier ou deja un objet populé.
const idVal = (v) => {
  if (v && typeof v === 'object' && v._id !== undefined) return String(v._id);
  return v === undefined || v === null ? '' : String(v);
};

// Cle metier d un document : niveau_id / filiere_id / annee_id, sauf si absent.
const cleVal = (v) => (v === undefined || v === null ? '' : String(v));

// Rapprochement compte utilisateur <-> professeur de l emploi du temps. Le lien numerote du
// compte (user.enseignant_id = matricule ENS055) recoit le professeur *055, alors que le nom
// ou l email sont faillibles (homonymes, fautes de frappe). On priorise donc ce lien, comme
// sur le tableau de bord, sinon une mauvaise correspondance masque des matieres.
const compactText = (s) => String(s || '').toLowerCase().normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
const numeroFin = (s) => {
  const m = String(s || '').match(/(\d+)\s*$/);
  return m ? m[1].replace(/^0+/, '') : '';
};
const trouverProf = (profs, u = {}) => {
  const lien = String(u.enseignant_id || '');
  if (lien) {
    const parLien = profs.filter(p => String(p.enseignantId || '') === lien);
    if (parLien.length) return parLien[0];
    const numero = numeroFin(lien);
    const parNumero = profs.filter(p => numero && numeroFin(p.id) === numero);
    if (parNumero.length) return parNumero[0];
  }
  const email = compactText(u.email);
  const nomComplet = compactText(`${u.prenom || ''} ${u.nom || ''}`);
  const nomSeul = compactText(u.nom);
  return profs.filter(p => {
    if (email && compactText(p.email) === email) return true;
    const cible = compactText(p.name);
    if (!cible) return false;
    if (nomComplet && (cible === nomComplet || compactText(p.short) === nomComplet)) return true;
    if (nomSeul && nomSeul.length >= 3 && cible.includes(nomSeul)) return true;
    return false;
  }).sort((a, b) => {
    // en cas d homonymie, la correspondance d email prime sur l approximation par le nom
    const rang = (p) => (email && compactText(p.email) === email ? 0 : 1);
    return rang(a) - rang(b);
  })[0] || null;
};

const FaireAppel = () => {
  const { user, isEnseignant, isAdmin, isDirecteur, isScolarite } = usePermissions();
  const [searchParams] = useSearchParams();
  const autoFromSuiviDone = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [seances, setSeances] = useState([]);
  // Miroir synchrone de la liste des seances. Quand on cree une seance depuis l emploi du
  // temps, loadEtudiants est appele juste apres setSeances : l etat React n est pas encore
  // re-rendu et la lecture de "seances" verrait l ancienne liste (seance introuvable, donc
  // aucun etudiant). Le ref, mis a jour avant le setState, contourne ce retard.
  const seancesRef = useRef([]);
  const majSeances = (valeur) => {
    const suivante = typeof valeur === 'function' ? valeur(seancesRef.current) : valeur;
    seancesRef.current = suivante;
    setSeances(suivante);
  };
  const [selectedSeance, setSelectedSeance] = useState(null);
  const [etudiants, setEtudiants] = useState([]);
  const [presences, setPresences] = useState({});
  const [saving, setSaving] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showNouvelleSeance, setShowNouvelleSeance] = useState(false);
  const [loadingMatieres, setLoadingMatieres] = useState(false);
  const [modalMatieres, setModalMatieres] = useState([]);
  // Donnees de reference pour rattacher une seance a ses etudiants. Une classe porte
  // filiere_id, niveau_id et annee_id (cles metier), alors qu une inscription porte des
  // ObjectId : il faut donc charger filieres, niveaux et annees pour traduire l une
  // vers les autres. Sans cela on retombe sur une devinette a partir du nom du groupe.
  // Un ref plutot qu un etat : ces donnees ne sont jamais rendues, seulement lues au
  // moment du filtrage, et le ref evite qu un clic rapide survienne avant le re-render.
  const refs = useRef({ classes: [], filieres: [], niveaux: [], annees: [] });
  const refsPromise = useRef(null);
  const [listeInfo, setListeInfo] = useState(null);

  // Charge les donnees de reference une seule fois, et se reutilise si le premier
  // appel est encore en cours : loadEtudiants doit toujours attendre leur arrivee.
  const chargerRefs = () => {
    if (!refsPromise.current) {
      const headers = getAuthHeaders();
      refsPromise.current = Promise.all([
        axios.get('/api/emploi/classes', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/filieres', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/niveaux', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/annees', { headers, timeout: 8000 }).catch(() => ({ data: [] }))
      ]).then(([classesRes, filieresRes, niveauxRes, anneesRes]) => {
        // Si l une des quatre requetes a echoue, chaque .catch a rendu []. On ne peut pas
        // distinguer une liste vide d une erreur : on retente au passage suivant plutot
        // que de figer la page sur des donnees de reference incompletes.
        const complet = [classesRes, filieresRes, niveauxRes, anneesRes].every(r => Array.isArray(r.data));
        if (!complet) refsPromise.current = null;
        // La collection classes contient chaque classe en double document : sans
        // deduplication, le select des groupes proposerait chaque groupe deux fois.
        const uniq = (liste, cle) => {
          const vus = new Set();
          return liste.filter(x => {
            const k = cle(x);
            if (vus.has(k)) return false;
            vus.add(k);
            return true;
          });
        };
        refs.current = {
          classes: uniq(Array.isArray(classesRes.data) ? classesRes.data : [], c => String(c._id || c.id || c.name || c.nom)),
          filieres: uniq(Array.isArray(filieresRes.data) ? filieresRes.data : [], f => String(f._id || f.filiere_id)),
          niveaux: uniq(Array.isArray(niveauxRes.data) ? niveauxRes.data : [], n => String(n._id || n.niveau_id)),
          annees: uniq(Array.isArray(anneesRes.data) ? anneesRes.data : [], a => String(a._id || a.annee_id))
        };
      });
    }
    return refsPromise.current;
  };
  const [nouvelleSeance, setNouvelleSeance] = useState({
    matiere_id: '',
    cours_nom: '',
    classe_nom: '',
    date: new Date().toISOString().split('T')[0],
    heureDebut: '09:00',
    heureFin: '10:30',
    salle: ''
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadSeances();
  }, []);

  // Les donnees de reference sont statiques : on les lance des le montage.
  useEffect(() => { chargerRefs(); }, []);

  useEffect(() => {
    const fromSuivi = searchParams.get('fromSuivi');
    if (fromSuivi !== '1' || autoFromSuiviDone.current) return;
    if (loading) return;

    const date = searchParams.get('date');
    const heureDebut = searchParams.get('heureDebut');
    const heureFin = searchParams.get('heureFin');
    const classeId = searchParams.get('classeId') || (searchParams.get('classeIds') || '').split(',').filter(Boolean)[0];
    const classeIds = (searchParams.get('classeIds') || '').split(',').filter(Boolean);
    const matiereId = searchParams.get('matiereId');
    const enseignantId = searchParams.get('enseignantId') || user?.enseignant_id || user?.id;

    if (!date || !classeId || !matiereId) return;

    const tryAuto = async () => {
      try {
        await chargerRefs();
        const toDateKeyLocal = (d) => {
          const dt = new Date(d);
          return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
        };
        const dk = toDateKeyLocal(date);

        let found = seancesRef.current.find(s => {
          const sdk = s.dateKey || toDateKeyLocal(s.date);
          return sdk === dk &&
            String(s.cours_id || '') === String(matiereId) &&
            ((Array.isArray(s.classe_ids) && s.classe_ids.some(id => String(id) === String(classeId))) || String(s.classe_id || '') === String(classeId));
        });

        if (found) {
          autoFromSuiviDone.current = true;
          if (found.fromEmploi) {
            const real = await ensureSeanceFromEmploi(found);
            loadEtudiants(real._id);
          } else {
            loadEtudiants(found._id);
            setSelectedSeance(found);
          }
          return;
        }

        const headers = getAuthHeaders();
        const empLike = {
          _id: `suivi_${dk}_${heureDebut}_${heureFin}_${matiereId}_${classeId}`,
          cardId: '',
          leconId: '',
          cours_id: matiereId,
          cours_nom: searchParams.get('matiereNom') || '',
          classe_id: classeId,
          classe_nom: searchParams.get('classeNom') || classeId,
          classe_ids: classeIds.length ? classeIds : [classeId],
          classe_noms: searchParams.get('classeNom') ? [searchParams.get('classeNom')] : [classeId],
          regime: 'NORMAL',
          enseignant_id: enseignantId,
          enseignant_nom: searchParams.get('enseignantNom') || `${user?.nom || ''} ${user?.prenom || ''}`.trim(),
          date: new Date(date + 'T00:00:00'),
          dateKey: dk,
          heureDebut: heureDebut || '',
          heureFin: heureFin || '',
          salle: '',
          type: 'COURS',
          fromEmploi: true
        };
        const real = await ensureSeanceFromEmploi(empLike);
        autoFromSuiviDone.current = true;
        loadEtudiants(real._id);
      } catch (e) {
        console.error('Auto from suivi failed', e);
      }
    };

    tryAuto();
  }, [searchParams, loading, seances, user]);

  const loadSeances = async () => {
    try {
      setLoading(true);
      const enseignantId = user?.enseignant_id || user?.id;
      const headers = getAuthHeaders();
      
      // Récupérer les séances de l'enseignant (rattrapage / créées manuellement)
      const res = await axios.get(
        `/api/presences/seances/enseignant/${enseignantId}`,
        { headers, timeout: 8000 }
      );
      let list = res.data || [];

      // Séances issues de l'emploi du temps (créneaux affectés au prof)
      if (isEnseignant) {
        const emploiList = await loadEmploiSeances();
        // Un cours regroupe deja appele pour un seul groupe : on complete la seance existante
        // avec tous ses groupes pour que l appel suivant liste bien tous les etudiants.
        const regimeSeance = (s) => (String(s.regime || 'NORMAL').toUpperCase() === 'TA' ? 'TA' : 'NORMAL');
        const parCle = new Map(emploiList.map(e => [`${e.cours_id}|${e.classe_id}|${e.dateKey}|${e.regime || 'NORMAL'}`, e]));
        list = list.map(s => {
          if (s.fromEmploi || s.estRattrapage) return s;
          const e = parCle.get(`${s.cours_id}|${s.classe_id}|${toDateKey(s.date)}|${regimeSeance(s)}`);
          if (!e || (e.classe_ids || []).length <= (s.classe_ids || []).length) return s;
          return { ...s, classe_ids: e.classe_ids, classe_noms: e.classe_noms, classe_nom: e.classe_nom, regime: e.regime };
        });
        const usedKeys = new Set(
          list.map(s => (!s.fromEmploi ? `${s.cours_id}|${s.classe_id}|${toDateKey(s.date)}|${regimeSeance(s)}` : ''))
        );
        const extra = emploiList.filter(e => !usedKeys.has(`${e.cours_id}|${e.classe_id}|${e.dateKey}|${e.regime || 'NORMAL'}`));
        const existingKeys = new Set(list.map(s => s._id));
        list = [...list, ...extra.filter(e => !existingKeys.has(e._id))];

        // Des seances ont pu etre creees une par groupe pour un meme cours : un enseignant ne
        // peut pas etre dans deux salles au meme creneau. On regroupe donc les lignes du meme
        // horaire, en reunissant les classes, et on garde de preference la seance reelle deja
        // enregistree (puis celle qui est validee) comme document de reference.
        const score = (s) => (s.fromEmploi ? 0 : 10) + (s.estValidee ? 1 : 0);
        const parSlot = new Map();
        for (const s of list) {
          const genre = s.estRattrapage ? 'R' : 'C';
          const cle = `${genre}|${s.regime || 'NORMAL'}|${toDateKey(s.date)}|${s.heureDebut}|${s.heureFin}`;
          const existant = parSlot.get(cle);
          if (!existant) {
            parSlot.set(cle, s);
            continue;
          }
          const ids = [...new Set([
            ...(existant.classe_ids || [existant.classe_id]),
            ...(s.classe_ids || [s.classe_id])
          ].filter(Boolean).map(String))];
          const noms = [...new Set([
            ...(existant.classe_noms || [existant.classe_nom]),
            ...(s.classe_noms || [s.classe_nom])
          ].filter(Boolean).map(String))];
          const base = score(s) > score(existant) ? s : existant;
          base.classe_ids = ids;
          base.classe_noms = noms;
          base.classe_id = base.classe_id || ids[0];
          base.classe_nom = noms.join(' / ');
          parSlot.set(cle, base);
        }
        list = [...parSlot.values()];
      }

      if (isEnseignant) {
        const uid = user?.enseignant_id || user?.id || user?._id || user?.userId;
        const unom = String((user?.nom || '') + ' ' + (user?.prenom || '')).toLowerCase().trim();
        const auj = toDateKey(new Date());
        list = list.filter((s) => {
          const dk = s.dateKey || toDateKey(s.date);
          if (dk !== auj) return false;
          const sid = s.enseignant_id || s.enseignantId || s.professeur_id || s.teacherId || s.profId;
          if (sid && uid && String(sid) === String(uid)) return true;
          if (sid && !uid) {
            // if we don't have uid, try to match by name from the prof we found
          }
          const snom = String(s.enseignant_nom || s.professeur_nom || s.nomProf || '').toLowerCase();
          if (unom && snom && snom.includes(unom)) return true;
          return false;
        });
      }

      list.sort((a, b) => new Date(a.date) - new Date(b.date));
      majSeances(list);
      setError('');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les séances');
    } finally {
      setLoading(false);
    }
  };

  const toDateKey = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Fenetre de la semaine civile en cours (lundi -> dimanche), comparee en AAAA-MM-JJ.
  const { debutSemaine, finSemaine } = (() => {
    const debut = new Date();
    debut.setHours(0, 0, 0, 0);
    debut.setDate(debut.getDate() - ((debut.getDay() + 6) % 7));
    const fin = new Date(debut);
    fin.setDate(fin.getDate() + 6);
    return { debutSemaine: toDateKey(debut), finSemaine: toDateKey(fin) };
  })();

  const seancesSemaine = seances.filter((s) => {
    const dk = s.dateKey || toDateKey(s.date);
    return Boolean(dk) && dk >= debutSemaine && dk <= finSemaine;
  });

  // Pour l'enseignant qui fait l'appel, on ne propose que SES propres seances du JOUR en cours.
  const aujKey = toDateKey(new Date());
  const uidEns = user?.enseignant_id || user?.id || user?._id || user?.userId;
  const unomEns = String((user?.nom || '') + ' ' + (user?.prenom || '')).toLowerCase().trim();
  const seancesListe = isEnseignant
    ? seances.filter((s) => {
        const dk = s.dateKey || toDateKey(s.date);
        if (dk !== aujKey) return false;
        const sid = s.enseignant_id || s.enseignantId || s.professeur_id || s.teacherId || s.profId;
        if (uidEns && sid && String(sid) === String(uidEns)) return true;
        const snom = String(s.enseignant_nom || s.professeur_nom || s.nomProf || '').toLowerCase();
        if (unomEns && snom && snom.includes(unomEns)) return true;
        return false;
      })
    : seancesSemaine;

  // Date du creneau dans la semaine civile en cours (lundi -> dimanche), et non sa prochaine
  // occurrence. Une seance de lundi consultee un jeudi doit porter sur lundi dernier, pas sur
  // lundi prochain : sinon seancesSemaine (borne lundi->dimanche) la rejette et la matiere
  // disparait de la liste au fil de la semaine.
  const getDateDansSemaine = (jour) => {
    const daysIndex = { lundi: 1, mardi: 2, mercredi: 3, jeudi: 4, vendredi: 5, samedi: 6, dimanche: 0 };
    const d = new Date();
    d.setHours(8, 0, 0, 0);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // lundi de la semaine en cours
    d.setDate(d.getDate() + ((daysIndex[jour] + 6) % 7)); // decalage depuis lundi
    return d;
  };

  const loadEmploiSeances = async () => {
    try {
      const headers = getAuthHeaders();
      // Un cours peut avoir ete modifie pour une date precisee (matiere, enseignant, salle).
      // Ces exceptions priment sur la grille hebdomadaire : on les charge avec la semaine.
      const exceptionsRes = await axios
        .get(`/api/suivi/exceptions?debut=${debutSemaine}&fin=${finSemaine}`, { headers, timeout: 8000 })
        .catch(() => ({ data: { exceptions: [] } }));
      const exceptions = (exceptionsRes.data && exceptionsRes.data.exceptions) || [];

      const [profsRes, leconsRes, cardsRes, classesRes] = await Promise.all([
        axios.get('/api/emploi/professeurs', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/lecons', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/cards', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/classes', { headers, timeout: 8000 }).catch(() => ({ data: [] }))
      ]);
      const allProfs = profsRes.data || [];
      const allLecons = leconsRes.data || [];
      const allCards = cardsRes.data || [];
      const allClasses = classesRes.data || [];

      const prof = trouverProf(allProfs, user);
      if (!prof) return [];
      const profId = prof.id;

      // Une lecon designe parfois le professeur par son lien numerote (ENS-055) et, ailleurs,
      // par l identifiant de la collection professeurs (*55). On accepte les deux formes pour
      // ne pas perdre une partie des matieres de l enseignant.
      const memeProf = (teacherId) => {
        const t = String(teacherId || '');
        if (t === String(profId)) return true;
        const cible = String(prof.enseignantId || '');
        if (cible && t === cible) return true;
        const n = numeroFin(profId);
        return Boolean(n) && t.startsWith('ENS-') && numeroFin(t) === n;
      };
      const leconsProf = allLecons.filter(l => memeProf(l.teacherId));
      if (leconsProf.length === 0) return [];

      const horairesNormal = {
        0: { debut: '09:00', fin: '10:30' },
        1: { debut: '10:45', fin: '12:15' },
        2: { debut: '13:15', fin: '14:45' },
        3: { debut: '15:00', fin: '16:30' }
      };
      const horairesTA = {
        0: { debut: '19:00', fin: '20:30' },
        1: { debut: '20:30', fin: '22:00' },
        2: { debut: '09:30', fin: '11:00' },
        3: { debut: '11:00', fin: '12:30' },
        4: { debut: '13:30', fin: '15:00' },
        5: { debut: '15:00', fin: '16:30' }
      };
      const daysBits = { 1: 'lundi', 2: 'mardi', 4: 'mercredi', 8: 'jeudi', 16: 'vendredi', 32: 'samedi', 64: 'dimanche' };
      const classeParId = (id) => allClasses.find(c => String(c.id || c._id) === String(id));
      const regimeDeCarte = (card, lecon, classIds) => {
        const brut = card.regime || lecon.regime;
        if (brut) return String(brut).toUpperCase() === 'TA' ? 'TA' : 'NORMAL';
        const ta = classIds.some((id) => String(classeParId(id)?.regime || '').toUpperCase() === 'TA');
        return ta ? 'TA' : 'NORMAL';
      };

      const list = [];
      for (const lecon of leconsProf) {
        const leconCards = allCards.filter(c => c.lessonId === lecon.id);
        for (const card of leconCards) {
          const classIds = (lecon.classIds || []).map(String);
          if (classIds.length === 0) continue;
          const regime = regimeDeCarte(card, lecon, classIds);
          const horairesMap = regime === 'TA' ? horairesTA : horairesNormal;
          const period = parseInt(card.period) || 0;
          const duree = parseInt(card.duree) || 1;
          const horaire = horairesMap[period];
          if (!horaire) continue;
          const horaireFin = (duree === 2 && horairesMap[period + 1]) ? horairesMap[period + 1].fin : horaire.fin;
          const daysBit = parseInt(card.days) || 0;
          // Un cours regroupe porte plusieurs classes sur la meme lecon : on garde la liste
          // complete pour proposer un seul appel et compter une seule seance.
          const classesNoms = classIds.map((id) => classeParId(id)?.name || id);
          for (const [bit, jour] of Object.entries(daysBits)) {
            if (daysBit & parseInt(bit)) {
              const dateObj = getDateDansSemaine(jour);
              list.push({
                _id: `emploi_${card._id || card.lessonId || Date.now()}_${classIds.join('_')}_${jour}`,
                cardId: String(card._id || card.id || ''),
                leconId: String(lecon.id || lecon._id || ''),
                cours_id: lecon.subjectId || '',
                cours_nom: lecon.subjectName || 'Cours',
                classe_id: classIds[0],
                classe_nom: classesNoms.join(' / '),
                classe_ids: classIds,
                classe_noms: classesNoms,
                regime,
                enseignant_id: user?.enseignant_id || user?.id,
                enseignant_nom: prof.name || '',
                date: dateObj,
                dateKey: toDateKey(dateObj),
                heureDebut: horaire.debut,
                heureFin: horaireFin,
                salle: card.classroomName || '',
                type: 'COURS',
                fromEmploi: true
              });
            }
          }
        }
      }

      // Des lecons distinctes peuvent encoder le meme cours regroupe au meme creneau. On les
      // reunit pour n avoir qu une seule seance, avec tous les groupes, dans la liste d appel.
      // Un enseignant ne peut pas assurer deux cours en meme temps : la cle ne porte donc que
      // la date et l horaire, ce qui compte une seule seance comme la synthese.
      const parCreneau = new Map();
      for (const item of list) {
        const cle = `${item.regime || 'NORMAL'}|${item.dateKey}|${item.heureDebut}|${item.heureFin}`;
        const existant = parCreneau.get(cle);
        if (!existant) {
          parCreneau.set(cle, item);
          continue;
        }
        const ids = [...new Set([...(existant.classe_ids || []), ...(item.classe_ids || [])])];
        const noms = ids.map((id) => {
          const classe = allClasses.find(c => String(c.id || c._id) === String(id));
          return classe?.name || String(id);
        });
        existant.classe_ids = ids;
        existant.classe_noms = noms;
        existant.classe_id = ids[0];
        existant.classe_nom = noms.join(' / ');
      }

      // Exceptions datees : elles priment sur la grille. Un cours reassigne a un autre
      // enseignant disparait de la semaine de l ancien, et apparait chez le nouveau.
      const estMoi = (ex) => memeProf(ex.profId) || memeProf(ex.enseignantId);
      const parCle = new Map();
      for (const ex of exceptions) {
        parCle.set(`${ex.cardId}|${ex.date}`, ex);
      }
      const semaine = [];
      for (const item of [...parCreneau.values()]) {
        const ex = parCle.get(`${item.cardId}|${item.dateKey}`);
        if (!ex) { semaine.push(item); continue; }
        if (!estMoi(ex)) continue; // le cours n est plus assure par cet enseignant a cette date
        const classIds = (ex.classIds || []).map(String);
        const classesNoms = (ex.classesNoms || []).map(String);
        semaine.push({
          ...item,
          cours_id: ex.subjectId || item.cours_id,
          cours_nom: ex.subjectName || item.cours_nom,
          classe_id: classIds[0] || item.classe_id,
          classe_nom: classesNoms.join(' / ') || item.classe_nom,
          classe_ids: classIds.length ? classIds : item.classe_ids,
          classe_noms: classesNoms.length ? classesNoms : item.classe_noms,
          heureDebut: ex.heureDebut || item.heureDebut,
          heureFin: ex.heureFin || item.heureFin,
          salle: ex.classroomName || item.salle,
          dateModifie: true
        });
      }
      // Les cours reassignes a cet enseignant et absents de sa grille hebdomadaire.
      for (const ex of exceptions) {
        if (!estMoi(ex)) continue;
        if (semaine.some(item => item.cardId === ex.cardId && item.dateKey === ex.date)) continue;
        const classIds = (ex.classIds || []).map(String);
        const classesNoms = (ex.classesNoms || []).map(String);
        const [annee, mois, jour] = String(ex.date || '').split('-');
        const dateObj = new Date(Number(annee), Number(mois) - 1, Number(jour), 8, 0, 0, 0);
        semaine.push({
          _id: `exception_${ex.cardId}_${ex.date}`,
          cardId: ex.cardId,
          leconId: ex.leconId,
          cours_id: ex.subjectId || '',
          cours_nom: ex.subjectName || 'Cours',
          classe_id: classIds[0] || '',
          classe_nom: classesNoms.join(' / '),
          classe_ids: classIds,
          classe_noms: classesNoms,
          regime: ex.regime || 'NORMAL',
          enseignant_id: user?.enseignant_id || user?.id,
          enseignant_nom: prof.name || '',
          date: dateObj,
          dateKey: toDateKey(dateObj),
          heureDebut: ex.heureDebut || '',
          heureFin: ex.heureFin || '',
          salle: ex.classroomName || '',
          type: 'COURS',
          fromEmploi: true,
          dateModifie: true
        });
      }
      return semaine;
    } catch (err) {
      console.error('Erreur chargement séances emploi du temps:', err);
      return [];
    }
  };

  const ensureSeanceFromEmploi = async (emp) => {
    const headers = getAuthHeaders();
    const regimeEmp = String(emp.regime || 'NORMAL').toUpperCase() === 'TA' ? 'TA' : 'NORMAL';
    const existing = seancesRef.current.find(s =>
      !s.fromEmploi &&
      s.cours_id === emp.cours_id &&
      s.classe_id === emp.classe_id &&
      String(s.regime || 'NORMAL').toUpperCase() === regimeEmp &&
      toDateKey(s.date) === emp.dateKey
    );
    if (existing) {
      setSelectedSeance(existing);
      return existing;
    }
    const payload = {
      cours_id: emp.cours_id,
      cours_nom: emp.cours_nom,
      enseignant_id: user?.enseignant_id || user?.id,
      enseignant_nom: `${user?.nom || ''} ${user?.prenom || ''}`.trim() || 'Enseignant',
      classe_id: emp.classe_id,
      classe_nom: emp.classe_nom,
      classe_ids: emp.classe_ids || [emp.classe_id],
      classe_noms: emp.classe_noms || [emp.classe_nom],
      regime: regimeEmp,
      date: `${emp.dateKey}T00:00:00`,
      heureDebut: emp.heureDebut,
      heureFin: emp.heureFin,
      salle: emp.salle || '',
      type: emp.type || 'COURS'
    };
    const res = await axios.post('/api/presences/seances', payload, { headers, timeout: 8000 });
    const created = res.data;
    majSeances(prev => [...prev.filter(s => s._id !== emp._id), created]);
    setSelectedSeance(created);
    return created;
  };

  // Retrouve une classe par son identifiant metier ("*3") ou son _id Mongo.
  const resoudreClasseParId = (classId) => {
    const cid = cleVal(classId);
    if (!cid) return null;
    return refs.current.classes.find(c => idVal(c.id) === cid || idVal(c._id) === cid) || null;
  };

  // Retrouve la classe qui porte le rattachement (filiere_id / niveau_id / annee_id).
  // Priorité à l identifiant : une séance créée à la main n a que du texte.
  const resoudreClasse = (seance) => {
    const classes = refs.current.classes;
    if (seance.classe_id) {
      // La seance porte l identifiant metier de la classe ("*3"), pas son _id Mongo : on teste
      // les deux, sinon on retombe sur la correspondance par nom, plus fragile (accents).
      const parId = resoudreClasseParId(seance.classe_id);
      if (parId) return parId;
    }
    const cible = normText(seance.classe_nom);
    if (!cible || cible === '*') return null;
    return classes.find(c => normText(c.name || c.nom || c.intitule) === cible) || null;
  };

  // L année de la classe quand elle existe, sinon celle de sa filière ou de son niveau,
  // sinon l année active. Plusieurs niveaux et plusieurs filières portent le même libellé
  // année après année : sans année, un groupe cumulerait les inscriptions de toutes les
  // promotions. Or plusieurs classes n ont pas d annee_id (L3 Dév G1, L2 MASI, L3 MASI
  // EAF/Logistique) et aucune année n est parfois marquée active : leur filière (ou leur
  // niveau) porte alors l année à retenir (ex. filière 83 « Dev informatique » -> 2026/2027).
  const resoudreAnnee = (classe, filiere, niveau) => {
    const annees = refs.current.annees;
    const chercher = (valeur) => {
      const cle = cleVal(valeur);
      if (!cle) return null;
      return annees.find(a => cleVal(a.annee_id) === cle || idVal(a._id) === cle) || null;
    };
    return (
      chercher(classe && classe.annee_id) ||
      chercher(filiere && filiere.annee_id) ||
      chercher(niveau && niveau.annee_id) ||
      annees.find(a => a.estActive) ||
      null
    );
  };

  const loadEtudiants = async (seanceId) => {
    try {
      const headers = getAuthHeaders();
      // Les classes, filieres, niveaux et annees doivent etre la avant le filtrage.
      await chargerRefs();
      
      const [presRes, etudiantsRes, inscriptionsRes] = await Promise.all([
        axios.get(`/api/presences/seance/${seanceId}`, { headers, timeout: 8000 }),
        // Pas de limite basse : la base contient plusieurs centaines d etudiants, et un
        // etudiant hors de ce chargement serait introuvable meme avec le bon filtre.
        axios.get('/api/etudiants?limit=100000', { headers, timeout: 8000 }),
        axios.get('/api/inscriptions', { headers, timeout: 8000 }).catch(() => ({ data: null }))
      ]);
      
      const presencesData = presRes.data || [];
      const presencesMap = {};
      presencesData.forEach(p => {
        presencesMap[p.etudiant_id] = {
          present: p.present,
          retard: p.retard || 0,
          heureArrivee: p.heureArrivee || '',
          observation: p.observation || '',
          id: p._id
        };
      });
      
      const seance = seancesRef.current.find(s => s._id === seanceId);
      if (seance) {
        const allEtudiants = etudiantsRes.data?.data || [];
        const inscriptionsData = inscriptionsRes.data;
        const inscriptions = (Array.isArray(inscriptionsData) ? inscriptionsData : inscriptionsData?.data) || [];
        const inscriptionsChargees = inscriptionsData !== null && inscriptionsData !== undefined;

        // On n identifie plus le groupe a partir de son nom. Le test precedent comparait
        // le nom de la classe a des extraits de libelles : il suffisait d un accent de
        // difference ("L3 Dev" du niveau contre "L3 Dév" de la classe) pour que rien ne
        // tombe juste, et surtout le repli, quand rien ne correspondait, affichait
        // silencieusement TOUS les etudiants charges. On part maintenant des classes
        // rattachees a la seance, qui portent deja filiere_id, niveau_id et annee_id.
        // Un cours regroupe en porte plusieurs : on reunit les etudiants de tous les groupes.
        const ids = new Set();
        const etudiantClasse = new Map();
        let info = null;
        const classeIds = Array.isArray(seance.classe_ids) && seance.classe_ids.length > 0
          ? seance.classe_ids
          : (seance.classe_id ? [seance.classe_id] : []);
        const cibles = classeIds
          .map(cid => ({ cid: String(cid), classe: resoudreClasseParId(cid) }))
          .filter(x => x.classe);
        if (cibles.length === 0) {
          const seule = resoudreClasse(seance);
          if (seule) cibles.push({ cid: String(seance.classe_id || seule.id || seule._id), classe: seule });
        }
        const libelleGroupe = seance.classe_nom || seance.classe_id || '—';

        if (!inscriptionsChargees) {
          info = {
            severity: 'error',
            texte: 'Impossible de charger les inscriptions : la liste ne peut pas être limitée au groupe, elle n\'est donc pas affichée.'
          };
        } else if (cibles.length === 0) {
          info = {
            severity: 'error',
            texte: `Groupe « ${libelleGroupe} » introuvable : impossible d'identifier les étudiants inscrits.`
          };
        } else {
          const erreurs = [];
          const details = [];
          let anneeIncertaine = false;

          for (const { cid, classe } of cibles) {
            const filiere = refs.current.filieres.find(f => cleVal(f.filiere_id) === cleVal(classe.filiere_id));
            const niveau = refs.current.niveaux.find(n => cleVal(n.niveau_id) === cleVal(classe.niveau_id));
            const annee = resoudreAnnee(classe, filiere, niveau);
            const nomGroupe = classe.name || classe.nom || classe.intitule || libelleGroupe;

            if (!filiere) {
              erreurs.push(`Filière « ${classe.filiere_id || '—'} » introuvable pour ${nomGroupe}`);
              continue;
            }
            if (!niveau) {
              erreurs.push(`Niveau « ${classe.niveau_id || '—'} » introuvable pour ${nomGroupe}`);
              continue;
            }
            if (!annee) anneeIncertaine = true;

            inscriptions.forEach(ins => {
              if (ins.statut && ins.statut !== 'CONFIRMEE') return;
              if (idVal(ins.filiere_id) !== idVal(filiere._id)) return;
              if (idVal(ins.niveau_id) !== idVal(niveau._id)) return;
              if (annee && idVal(ins.annee_universitaire_id) !== idVal(annee._id)) return;
              const id = ins.etudiant?._id || ins.etudiant_id;
              if (id) {
                ids.add(String(id));
                if (!etudiantClasse.has(String(id))) {
                  etudiantClasse.set(String(id), { cid, nom: nomGroupe });
                }
              }
            });

            details.push([
              `filière ${filiere.intitule || filiere.filiere_id}`,
              `niveau ${niveau.intitule || niveau.niveau_id}`,
              annee ? `année ${annee.intitule || annee.nom || ''}` : 'toutes années'
            ].join(' · '));
          }

          const nomsGroupes = cibles
            .map(({ classe }) => classe.name || classe.nom || classe.intitule || '—')
            .join(' / ');

          if (erreurs.length > 0 && ids.size === 0) {
            info = { severity: 'error', texte: `${erreurs.join(' ; ')}.` };
          } else if (ids.size === 0) {
            info = {
              severity: 'warning',
              texte: `Aucun étudiant inscrit pour ${nomsGroupes} (${details.join(' | ')}).`
            };
          } else if (erreurs.length > 0) {
            info = {
              severity: 'warning',
              texte: `${ids.size} étudiant(s) inscrit(s) · ${nomsGroupes} · ${details.join(' | ')} ; ${erreurs.join(' ; ')}.`
            };
          } else if (anneeIncertaine) {
            info = {
              severity: 'warning',
              texte: `Année active introuvable : ${ids.size} étudiant(s) pour ${nomsGroupes} (${details.join(' | ')}), risque de cumul de plusieurs promotions.`
            };
          } else {
            info = {
              severity: 'info',
              texte: `${ids.size} étudiant(s) inscrit(s) · ${nomsGroupes} · ${details.join(' | ')}`
            };
          }
        }

        setListeInfo(info);

        const rawEtudiants = ids.size > 0
          ? allEtudiants.filter(e => ids.has(String(e._id)))
          : [];

        const etudiantsData = rawEtudiants.map(e => {
          const groupe = etudiantClasse.get(String(e._id));
          return {
            etudiant_id: e._id,
            etudiant_nom: e.Nom || '',
            etudiant_prenom: e.Prénom || '',
            etudiant_cne_masar: e.CNE || '',
            etudiant_classe_id: groupe?.cid || seance.classe_id || '',
            etudiant_classe_nom: groupe?.nom || seance.classe_nom || ''
          };
        });
        setEtudiants(etudiantsData);
        
        const initialPresences = {};
        etudiantsData.forEach(e => {
          if (presencesMap[e.etudiant_id]) {
            initialPresences[e.etudiant_id] = presencesMap[e.etudiant_id];
          } else {
            initialPresences[e.etudiant_id] = {
              present: true,
              retard: 0,
              heureArrivee: '',
              observation: '',
              id: null
            };
          }
        });
        setPresences(initialPresences);
      } else {
        setEtudiants([]);
        setPresences({});
        setListeInfo(null);
      }
      
      setSelectedSeance(seance);
    } catch (err) {
      console.error('Erreur:', err);
      setEtudiants([]);
      setListeInfo({ severity: 'error', texte: 'Impossible de charger les étudiants.' });
      setError('Impossible de charger les étudiants');
    }
  };

  const handleTogglePresence = (etudiantId) => {
    setPresences(prev => ({
      ...prev,
      [etudiantId]: {
        ...prev[etudiantId],
        present: !prev[etudiantId]?.present
      }
    }));
  };

  const handleRetardChange = (etudiantId, retard) => {
    setPresences(prev => ({
      ...prev,
      [etudiantId]: {
        ...prev[etudiantId],
        retard: parseInt(retard) || 0
      }
    }));
  };

  const handleObservationChange = (etudiantId, observation) => {
    setPresences(prev => ({
      ...prev,
      [etudiantId]: {
        ...prev[etudiantId],
        observation
      }
    }));
  };

  const handleSavePresences = async () => {
    if (!selectedSeance) return;
    
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      const data = {
        seance_id: selectedSeance._id,
        presences: Object.entries(presences).map(([etudiantId, p]) => ({
          etudiant_id: etudiantId,
          etudiant_nom: etudiants.find(e => e.etudiant_id === etudiantId)?.etudiant_nom || '',
          etudiant_prenom: etudiants.find(e => e.etudiant_id === etudiantId)?.etudiant_prenom || '',
          etudiant_cne: etudiants.find(e => e.etudiant_id === etudiantId)?.etudiant_cne_masar || '',
          cours_id: selectedSeance.cours_id,
          cours_nom: selectedSeance.cours_nom,
          classe_id: etudiants.find(e => e.etudiant_id === etudiantId)?.etudiant_classe_id || selectedSeance.classe_id,
          classe_nom: etudiants.find(e => e.etudiant_id === etudiantId)?.etudiant_classe_nom || selectedSeance.classe_nom,
          enseignant_id: selectedSeance.enseignant_id,
          present: p.present,
          retard: p.retard || 0,
          heureArrivee: p.heureArrivee || '',
          observation: p.observation || ''
        }))
      };
      
      const res = await axios.post(
        '/api/presences/enregistrer',
        data,
        { headers, timeout: 8000 }
      );
      
      setSuccess('Présences enregistrées avec succès !');
      setTimeout(() => setSuccess(''), 3000);
      
      // Recharger les données
      await loadSeances();
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenNouvelleSeance = async () => {
    setShowNouvelleSeance(true);
    setLoadingMatieres(true);
    try {
      const headers = getAuthHeaders();
      // Les groupes proposes doivent etre charges avant l ouverture du select.
      await chargerRefs();
      const enseignantId = user?.enseignant_id || user?.id;
      let matieres = [];
      if (enseignantId) {
        const res = await axios.get(`/api/matieres/enseignant/${enseignantId}`, { headers, timeout: 8000 });
        matieres = res.data || [];
      }
      if (matieres.length === 0) {
        const res = await axios.get('/api/matieres', { headers, timeout: 8000 });
        matieres = res.data?.data || res.data || [];
      }
      setModalMatieres(matieres);
    } catch (err) {
      console.error('Erreur chargement matières:', err);
      setModalMatieres([]);
    } finally {
      setLoadingMatieres(false);
    }
  };

  const handleCreateSeance = async () => {
    if (!nouvelleSeance.matiere_id || !nouvelleSeance.classe_nom.trim()) {
      setError('Veuillez sélectionner une matière et saisir le groupe/classe');
      setTimeout(() => setError(''), 4000);
      return;
    }
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      const matiere = modalMatieres.find(m => (m._id || m.id) === nouvelleSeance.matiere_id);
      // On mémorise l identifiant de la classe, pas seulement son libellé : c est lui qui
      // permet ensuite de retrouver filiere, niveau et année sans deviner a partir du texte.
      const classeCible = refs.current.classes.find(c =>
        normText(c.name || c.nom || c.intitule) === normText(nouvelleSeance.classe_nom)
      );
      const data = {
        cours_id: nouvelleSeance.matiere_id,
        cours_nom: matiere ? `${matiere.code || ''} ${matiere.nom || matiere.name || ''}`.trim() : nouvelleSeance.cours_nom,
        enseignant_id: user?.enseignant_id || user?.id,
        enseignant_nom: `${user?.nom || ''} ${user?.prenom || ''}`.trim() || 'Enseignant',
        classe_id: classeCible ? String(classeCible._id || classeCible.id) : nouvelleSeance.classe_nom.trim(),
        classe_nom: nouvelleSeance.classe_nom.trim(),
        date: nouvelleSeance.date,
        heureDebut: nouvelleSeance.heureDebut,
        heureFin: nouvelleSeance.heureFin,
        salle: nouvelleSeance.salle || '',
        type: 'COURS'
      };
      await axios.post('/api/presences/seances', data, { headers, timeout: 8000 });
      setSuccess('Séance créée avec succès !');
      setTimeout(() => setSuccess(''), 3000);
      setShowNouvelleSeance(false);
      setNouvelleSeance({
        matiere_id: '',
        cours_nom: '',
        classe_nom: '',
        date: new Date().toISOString().split('T')[0],
        heureDebut: '09:00',
        heureFin: '10:30',
        salle: ''
      });
      await loadSeances();
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.error || 'Erreur lors de la création de la séance');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    if (!selectedSeance) return;
    const printWindow = window.open('', '_blank');
    const esc = (s = '') => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const fmtDate = (d) => new Date(d).toLocaleDateString('fr-FR');
    const heure = selectedSeance.heureDebut && selectedSeance.heureFin
      ? `${selectedSeance.heureDebut} - ${selectedSeance.heureFin}`
      : '';

    let rows = '';
    etudiants.forEach((e, i) => {
      const p = presences[e.etudiant_id] || { present: true, retard: 0 };
      const present = p.present !== false;
      rows += `<tr>
        <td class="num">${i + 1}</td>
        <td>${esc(e.etudiant_nom)} ${esc(e.etudiant_prenom)}</td>
        <td>${esc(e.etudiant_cne_masar || '')}</td>
        <td class="center">${present ? 'Présent' : 'Absent'}</td>
        <td class="center">${present && p.retard ? esc(p.retard) : ''}</td>
        <td>${esc(p.observation || '')}</td>
        <td></td>
      </tr>`;
    });

    const total = etudiants.length;
    const presents = etudiants.filter(e => (presences[e.etudiant_id] || { present: true }).present !== false).length;
    const absents = total - presents;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Fiche d'appel - ${esc(selectedSeance.cours_nom)}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; color: #1f2937; }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #3b82f6; padding-bottom: 12px; margin-bottom: 20px; }
          .header h1 { font-size: 22px; color: #1f2937; }
          .header .sub { color: #3b82f6; font-size: 13px; }
          .header .annee { font-size: 13px; color: #6b7280; text-align: right; }
          .title { text-align: center; font-size: 18px; font-weight: 700; margin-bottom: 16px; color: #111827; }
          .info-box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px 18px; margin-bottom: 20px; }
          .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px 20px; }
          .info-grid .item { font-size: 13px; }
          .info-grid .label { color: #6b7280; font-size: 11px; text-transform: uppercase; }
          .info-grid .value { font-weight: 600; margin-top: 2px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #3b82f6; color: white; padding: 9px 10px; text-align: left; font-size: 12px; text-transform: uppercase; }
          td { border: 1px solid #d1d5db; padding: 7px 10px; font-size: 13px; }
          tr:nth-child(even) td { background: #f9fafb; }
          td.num, td.center { text-align: center; }
          .stats { display: flex; justify-content: space-between; margin: 16px 0; font-size: 13px; font-weight: 600; }
          .stats .p { color: #059669; } .stats .a { color: #dc2626; }
          .signature { margin-top: 40px; display: flex; justify-content: space-between; font-size: 13px; }
          .signature .line { display: inline-block; border-top: 1px solid #6b7280; padding-top: 4px; min-width: 220px; text-align: center; }
          .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #9ca3af; }
          @media print { body { padding: 15px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>SUP2I - École Supérieure d'Informatique</h1>
            <div class="sub">Fiche d'appel / Fiche de présence</div>
          </div>
          <div class="annee">Année Universitaire 2026/2027<br/>Édité le ${new Date().toLocaleDateString('fr-FR')}</div>
        </div>
        <div class="title">FICHE D'APPEL</div>
        <div class="info-box">
          <div class="info-grid">
            <div class="item"><div class="label">Matière</div><div class="value">${esc(selectedSeance.cours_nom)}</div></div>
            <div class="item"><div class="label">Groupe / Classe</div><div class="value">${esc(selectedSeance.classe_nom)}</div></div>
            <div class="item"><div class="label">Enseignant</div><div class="value">${esc(selectedSeance.enseignant_nom)}</div></div>
            <div class="item"><div class="label">Date</div><div class="value">${fmtDate(selectedSeance.date)}</div></div>
            <div class="item"><div class="label">Horaire</div><div class="value">${esc(heure)}</div></div>
            <div class="item"><div class="label">Salle</div><div class="value">${esc(selectedSeance.salle || '-')}</div></div>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width:36px">N°</th>
              <th>Étudiant</th>
              <th style="width:110px">CNE</th>
              <th style="width:80px">Présence</th>
              <th style="width:70px">Retard (min)</th>
              <th>Observation</th>
              <th style="width:90px">Signature</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="stats">
          <span>Total : <b>${total}</b></span>
          <span class="p">Présents : <b>${presents}</b></span>
          <span class="a">Absents : <b>${absents}</b></span>
        </div>
        <div class="signature">
          <div><div class="line">L'enseignant</div></div>
          <div><div class="line">La direction</div></div>
        </div>
        <div class="footer">Document généré par la plateforme scolaire SUP2I</div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const getStatistiques = () => {
    const total = Object.keys(presences).length;
    const presents = Object.values(presences).filter(p => p.present).length;
    const absents = total - presents;
    return { total, presents, absents };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const stats = getStatistiques();
  // La scolarite consulte et imprime l appel, elle ne le modifie pas : canEdit reste faux
  // pour elle, meme sur une seance non encore validee.
  const canEdit = isAdmin || isDirecteur || (!isScolarite && !selectedSeance?.estValidee);

  return (
    <Box sx={{ p: 3 }}>
      {/* Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            {isScolarite ? '🔎 Consulter l\'appel' : "✅ Faire l'appel"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isScolarite ? 'Consultez les appels effectués et imprimez-les' : "Sélectionnez une séance pour faire l'appel"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadSeances}
          >
            Actualiser
          </Button>
          {!isScolarite && (
            <Button
              variant="contained"
              startIcon={<Schedule />}
              onClick={handleOpenNouvelleSeance}
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              Nouvelle séance
            </Button>
          )}
        </Stack>
      </Box>

      {/* Sélection de la séance */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Sélectionner une séance</InputLabel>
              <Select
                value={selectedSeance?._id || ''}
                onChange={async (e) => {
                  const seance = seances.find(s => s._id === e.target.value);
                  if (!seance) return;
                  if (seance.fromEmploi) {
                    try {
                      const real = await ensureSeanceFromEmploi(seance);
                      loadEtudiants(real._id);
                    } catch (err) {
                      console.error('Erreur création séance:', err.response?.data || err.message);
                      setError(err.response?.data?.error || err.message || 'Impossible de créer la séance pour l\'appel');
                    }
                  } else {
                    loadEtudiants(seance._id);
                  }
                }}
                label="Sélectionner une séance"
              >
                {seancesListe.map((s) => (
                  <MenuItem key={s._id} value={s._id}>
                    {s.cours_nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {selectedSeance && (
            <Grid item xs={12} sm={6} md={8}>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Chip
                  label={`📚 ${selectedSeance.cours_nom}`}
                  sx={{ bgcolor: '#dbeafe', color: '#1e40af' }}
                />
                <Chip
                  label={`🏫 ${selectedSeance.classe_nom}`}
                  sx={{ bgcolor: '#dcfce7', color: '#166534' }}
                />
                <Chip
                  label={`👨‍🏫 ${selectedSeance.enseignant_nom}`}
                  sx={{ bgcolor: '#fef3c7', color: '#92400e' }}
                />
                <Chip
                  label={`📅 ${new Date(selectedSeance.date).toLocaleDateString('fr-FR')}`}
                  sx={{ bgcolor: '#f3e8ff', color: '#7c3aed' }}
                />
              </Stack>
            </Grid>
          )}
        </Grid>
      </Paper>

      {selectedSeance && (
        <>
          {!canEdit && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {isScolarite
                ? 'Consultation uniquement : vous pouvez imprimer l\'appel, pas le modifier.'
                : 'Cet appel a déjà été validé. Seule la direction peut le modifier.'}
            </Alert>
          )}
          {/* Statistiques */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 2, background: '#dbeafe' }}>
                <CardContent>
                  <Typography variant="body2" color="#1e40af">Total étudiants</Typography>
                  <Typography variant="h4" fontWeight="bold" color="#1e40af">{stats.total}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 2, background: '#dcfce7' }}>
                <CardContent>
                  <Typography variant="body2" color="#166534">Présents</Typography>
                  <Typography variant="h4" fontWeight="bold" color="#166534">{stats.presents}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 2, background: '#fee2e2' }}>
                <CardContent>
                  <Typography variant="body2" color="#991b1b">Absents</Typography>
                  <Typography variant="h4" fontWeight="bold" color="#991b1b">{stats.absents}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {listeInfo && (
            <Alert severity={listeInfo.severity} sx={{ mb: 2, borderRadius: 2 }}>
              {listeInfo.texte}
            </Alert>
          )}

          {/* Liste des étudiants */}
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>N°</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Étudiant</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>CNE</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Présence</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Retard (min)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Observation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {etudiants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Aucun étudiant à afficher pour cette séance.
                    </TableCell>
                  </TableRow>
                )}
                {etudiants.map((etudiant, index) => {
                  const presence = presences[etudiant.etudiant_id] || { present: true, retard: 0 };
                  return (
                    <TableRow key={etudiant.etudiant_id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person fontSize="small" color="action" />
                          <Typography variant="body2">
                            {etudiant.etudiant_nom} {etudiant.etudiant_prenom}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{etudiant.etudiant_cne_masar || '-'}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={presence.present}
                          onChange={() => handleTogglePresence(etudiant.etudiant_id)}
                          disabled={!canEdit}
                          color={presence.present ? 'success' : 'error'}
                          icon={<Cancel />}
                          checkedIcon={<CheckCircle />}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="number"
                          value={presence.retard || 0}
                          onChange={(e) => handleRetardChange(etudiant.etudiant_id, e.target.value)}
                          disabled={!canEdit || !presence.present}
                          sx={{ width: 80 }}
                          inputProps={{ min: 0, max: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        {presence.present ? (
                          <Chip
                            label="✅ Présent"
                            size="small"
                            sx={{ bgcolor: '#dcfce7', color: '#166534' }}
                          />
                        ) : (
                          <Chip
                            label="❌ Absent"
                            size="small"
                            sx={{ bgcolor: '#fee2e2', color: '#991b1b' }}
                          />
                        )}
                        {presence.retard > 0 && presence.present && (
                          <Chip
                            label={`⏰ ${presence.retard}min de retard`}
                            size="small"
                            sx={{ bgcolor: '#fef3c7', color: '#92400e', ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={presence.observation || ''}
                          onChange={(e) => handleObservationChange(etudiant.etudiant_id, e.target.value)}
                          disabled={!canEdit}
                          placeholder="Sortie, matériel oublié..."
                          sx={{ width: 220 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Stack direction="row" spacing={2}>
              {!isScolarite && (
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                  onClick={handleSavePresences}
                  disabled={!canEdit || saving}
                  sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer les présences'}
                </Button>
              )}
              {!isScolarite && (
                <Button
                  variant="outlined"
                  startIcon={<QrCode />}
                  onClick={() => setShowQRCode(true)}
                >
                  Générer QR Code
                </Button>
              )}
            </Stack>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={handlePrint}
            >
              Imprimer
            </Button>
          </Box>
        </>
      )}

      {/* Modal Nouvelle séance */}
      <Dialog open={showNouvelleSeance} onClose={() => setShowNouvelleSeance(false)} maxWidth="sm" fullWidth>
        <DialogTitle>➕ Nouvelle séance</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Matière *</InputLabel>
              <Select
                value={nouvelleSeance.matiere_id}
                onChange={(e) => setNouvelleSeance({ ...nouvelleSeance, matiere_id: e.target.value })}
                label="Matière *"
                required
                disabled={loadingMatieres}
              >
                <MenuItem value="">Sélectionner</MenuItem>
                {modalMatieres.map((m) => (
                  <MenuItem key={m._id || m.id} value={m._id || m.id}>
                    {m.code || ''} - {m.nom || m.name || ''}
                  </MenuItem>
                ))}
              </Select>
              {modalMatieres.length === 0 && !loadingMatieres && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Aucune matière assignée à votre profil. Sélection possible après affectation par l'administration.
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Groupe / Classe *</InputLabel>
              <Select
                value={nouvelleSeance.classe_nom}
                onChange={(e) => setNouvelleSeance({ ...nouvelleSeance, classe_nom: e.target.value })}
                label="Groupe / Classe *"
                required
              >
                <MenuItem value="">Sélectionner</MenuItem>
                {refs.current.classes.map((c) => (
                  <MenuItem key={c._id || c.id} value={c.name || c.nom || ''}>
                    {c.name || c.nom || ''}
                  </MenuItem>
                ))}
              </Select>
              {refs.current.classes.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Aucun groupe disponible : vérifiez les classes dans les paramètres.
                </Typography>
              )}
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Date *"
                  type="date"
                  value={nouvelleSeance.date}
                  onChange={(e) => setNouvelleSeance({ ...nouvelleSeance, date: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Début *"
                  type="time"
                  value={nouvelleSeance.heureDebut}
                  onChange={(e) => setNouvelleSeance({ ...nouvelleSeance, heureDebut: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Fin *"
                  type="time"
                  value={nouvelleSeance.heureFin}
                  onChange={(e) => setNouvelleSeance({ ...nouvelleSeance, heureFin: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Salle"
              value={nouvelleSeance.salle}
              onChange={(e) => setNouvelleSeance({ ...nouvelleSeance, salle: e.target.value })}
              margin="normal"
              placeholder="Ex : Salle 101"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNouvelleSeance(false)}>Annuler</Button>
          <Button
            onClick={handleCreateSeance}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Créer la séance
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FaireAppel;


